import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import classNames from "classnames";
import css from "./style.scss";

export default function Tooltip({
  children,
  title,
  description,
  content,
  position = "top",
  xOffset = 0,
  yOffset = 0,
  arrowOffset = 60,
  width,
  bgColor = "#128292",
  contentClassName,
  closeIcon,
  // Opt-in click-only mode: false stops hover from opening the callout, so it
  // appears only when the trigger is clicked. The default keeps every existing
  // caller on hover-to-open.
  openOnHover = true,
}) {
  const [show, setShow] = useState(false);

  const triggerRef = useRef(null);
  const hoverLockedRef = useRef(false);

  const isOverTrigger = useRef(false);
  const isOverTooltip = useRef(false);

  const [coords, setCoords] = useState({ top: 0, left: 0 });

  const updatePos = () => {
    const rect = triggerRef.current.getBoundingClientRect();
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;

    let top = rect.top + scrollY;
    let left = rect.left + scrollX;

    if (position === "top") {
      top -= 10;
      left += rect.width / 2;
    }
    if (position === "bottom") {
      top += rect.height + 10;
      left += rect.width / 2;
    }
    if (position === "left") {
      top += rect.height / 2;
      left -= 10;
    }

    setCoords({ top: top + yOffset, left: left + xOffset });
  };

  useEffect(() => {
    if (show) updatePos();
  }, [show]);

  useEffect(() => {
    if (!show) return;
    // scroll events don't bubble, so listen on the capture phase to catch
    // scrolling on any ancestor container, not just the window itself
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [show]);
  
  const tryClose = () => {
    requestAnimationFrame(() => {
      if (!isOverTrigger.current && !isOverTooltip.current) {
        hoverLockedRef.current = false;
        setShow(false);
      }
    });
  };

  return (
    <>
      {/* TRIGGER */}
      <span
        ref={triggerRef}
        className={css.trigger}
        onMouseEnter={() => {
          isOverTrigger.current = true;
          if (openOnHover && !hoverLockedRef.current) {
            setShow(true);
          }
        }}
        onMouseLeave={() => {
          isOverTrigger.current = false;
          tryClose();
        }}
        onClick={() => {
          hoverLockedRef.current = false;
          setShow(prev => !prev);
        }}
      >
        {children}
      </span>

      {show &&
        ReactDOM.createPortal(
          <div
            className={classNames(css.tooltipWrapper, css[position])}
            style={{
              top: coords.top,
              left: coords.left,
              backgroundColor: bgColor,
              "--arrow-offset": typeof arrowOffset === "number" ? `${arrowOffset}px` : arrowOffset,
              "--tooltip-bg": bgColor,
              ...(width ? { width } : {}),
            }}
            onMouseEnter={() => {
              isOverTooltip.current = true;
            }}
            onMouseLeave={() => {
              isOverTooltip.current = false;
              tryClose();
            }}
          >
            {content ? (
              <div className={classNames(css.content, contentClassName)}>
                {content}
              </div>
            ) : (
              <>
                <div className={css.title}>
                  {title}
                  <span
                    className={css.close}
                    onClick={(e) => {
                      e.stopPropagation();
                      hoverLockedRef.current = true; // 🔒 block hover reopen
                      setShow(false);
                    }}
                  >
                    {closeIcon || <>&times;</>}
                  </span>
                </div>
                <div className={css.desc}>{description}</div>
              </>
            )}
          </div>,
          document.getElementById("tooltip-root")
        )}
    </>
  );
}
